import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

export const COLORS = ["#2f6bff", "#19b3a6", "#8b5cf6", "#f59e0b", "#ec4899", "#10b981"];

export const fmtHours = (sec: number) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h${m ? " " + m + "m" : ""}`;
};
const axisHours = (sec: number) => `${Math.round(sec / 3600)}h`;

export interface SeriesDef { key: string; name: string; color?: string; }

const grid = <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />;

export function Empty({ label = "No data in range." }: { label?: string }) {
  return <div className="empty">{label}</div>;
}

export function TrendLine({ data, xKey, lines, height = 260, kind = "line", unit = "count" }: {
  data: any[]; xKey: string; lines: SeriesDef[]; height?: number;
  kind?: "line" | "area"; unit?: "hours" | "count";
}) {
  if (!data || data.length === 0) return <Empty />;
  const yFmt = unit === "hours" ? axisHours : (v: number) => String(v);
  const tFmt = unit === "hours" ? fmtHours : (v: number) => String(v);
  const Chart: any = kind === "area" ? AreaChart : LineChart;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <Chart data={data} margin={{ top: 8, right: 18, left: 0, bottom: 2 }}>
        {grid}
        <XAxis dataKey={xKey} tickMargin={8} minTickGap={18} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={yFmt} width={46} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v: any, n: any) => [tFmt(Number(v)), n]} />
        {lines.length > 1 && <Legend />}
        {lines.map((l, i) => kind === "area" ? (
          <Area key={l.key} type="monotone" dataKey={l.key} name={l.name}
                stroke={l.color ?? COLORS[i % COLORS.length]}
                fill={l.color ?? COLORS[i % COLORS.length]} fillOpacity={0.12} strokeWidth={2} />
        ) : (
          <Line key={l.key} type="monotone" dataKey={l.key} name={l.name}
                stroke={l.color ?? COLORS[i % COLORS.length]} strokeWidth={2}
                dot={{ r: 2 }} activeDot={{ r: 5 }} />
        ))}
      </Chart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, height = 230 }: {
  data: { name: string; value: number; color: string }[]; height?: number;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius="56%" outerRadius="82%"
             paddingAngle={2} stroke="var(--surface)">
          {data.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Pie>
        <Tooltip formatter={(v: any, n: any) => [`${v} (${Math.round((Number(v) / total) * 100)}%)`, n]} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function BarSeries({ data, xKey, barKey, name, height = 240, unit = "count",
  color = COLORS[0], highlightKey }: {
  data: any[]; xKey: string; barKey: string; name: string; height?: number;
  unit?: "hours" | "count"; color?: string; highlightKey?: string;
}) {
  if (!data || data.length === 0) return <Empty />;
  const yFmt = unit === "hours" ? axisHours : (v: number) => String(v);
  const tFmt = unit === "hours" ? fmtHours : (v: number) => String(v);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 18, left: 0, bottom: 2 }}>
        {grid}
        <XAxis dataKey={xKey} tickMargin={8} minTickGap={6} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={yFmt} width={46} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v: any) => [tFmt(Number(v)), name]} cursor={{ fill: "var(--surface-2)" }} />
        <Bar dataKey={barKey} name={name} radius={[5, 5, 0, 0]} maxBarSize={48}>
          {data.map((d, i) => (
            <Cell key={i} fill={highlightKey && d[highlightKey] ? COLORS[0] : color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
